import React from 'react';
import { getPlaybookById } from '../../../data/playbookTaxonomy';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { 
  PresentationChartLineIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const StorytellingWithDataStage2 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 2: Tell the Story"
        icon={<PresentationChartLineIcon className="h-7 w-7 text-primary-light" />}
        heading="Data Storytelling"
      >
        <ExpandableCallout
          title="What do I need to DO in this stage?"
          type="info"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 3 things:</p>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1.</span>
                Create a compelling narrative
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2.</span>
                Visualise the data
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3.</span>
                Present with impact
              </li>
            </ol>
          </div>
        </ExpandableCallout>

        <PlaybookSection
          title="Create a Compelling Narrative"
          icon={<LightBulbIcon className="h-7 w-7 text-primary-light" />}
          heading="Story Structure"
        >
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Now that you have your data and business context, it's time to craft a story that will resonate with your audience. A good data story should have a clear structure and flow.
            </p>
          </div>

          <PlaybookCard className="mt-6">
            <h3 className="text-white font-semibold mb-4">Story Structure:</h3>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1.</span>
                <div>
                  <strong>Hook:</strong>
                  <p className="text-gray-300 mt-2">
                    Start with a compelling fact or question that grabs attention. This could be a surprising statistic or a thought-provoking question related to your data.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2.</span>
                <div>
                  <strong>Context:</strong>
                  <p className="text-gray-300 mt-2">
                    Provide background information that helps your audience understand the situation. This might include historical data, market trends, or company-specific context.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3.</span>
                <div>
                  <strong>Challenge:</strong>
                  <p className="text-gray-300 mt-2">
                    Present the problem or opportunity you've identified. Use data to illustrate the impact and urgency of addressing this challenge.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">4.</span>
                <div>
                  <strong>Solution:</strong>
                  <p className="text-gray-300 mt-2">
                    Outline your proposed solution or approach. Use data to support why this solution is likely to be effective.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">5.</span>
                <div>
                  <strong>Call to Action:</strong>
                  <p className="text-gray-300 mt-2">
                    End with a clear call to action. What do you want your audience to do next? Make it specific and actionable.
                  </p>
                </div>
              </li>
            </ol>
          </PlaybookCard>

          <ExpandableCallout
            title="Example Narrative"
            type="info"
            defaultOpen={true}
            className="mt-6"
          >
            <div className="space-y-4">
              <p className="text-gray-300">
                Using our previous example about employee retention:
              </p>
              <ol className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">1.</span>
                  <div>
                    <strong>Hook:</strong>
                    <p className="text-gray-300 mt-2">
                      "Did you know that a 20% increase in employee turnover is costing us $2M annually in recruitment and training costs?"
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">2.</span>
                  <div>
                    <strong>Context:</strong>
                    <p className="text-gray-300 mt-2">
                      "Over the past year, we've seen a significant increase in turnover among our high-performing teams. This trend is not only affecting our project timelines but also our bottom line."
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">3.</span>
                  <div>
                    <strong>Challenge:</strong>
                    <p className="text-gray-300 mt-2">
                      "Our team health checks reveal that 92% of teams are struggling with inter-team alignment, and 28% of teams have identified lack of manager support as a critical issue."
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">4.</span>
                  <div>
                    <strong>Solution:</strong>
                    <p className="text-gray-300 mt-2">
                      "By implementing a structured manager development program and improving our team alignment processes, we can reduce turnover by 15% within six months."
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light font-bold mr-2">5.</span>
                  <div>
                    <strong>Call to Action:</strong>
                    <p className="text-gray-300 mt-2">
                      "I recommend we allocate $500K to launch the manager development program next quarter. This investment will pay for itself within 12 months through reduced turnover costs."
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </ExpandableCallout>
        </PlaybookSection>

        <PlaybookSection
          title="Visualise the Data"
          icon={<ChartBarIcon className="h-7 w-7 text-primary-light" />}
          heading="Data Visualization"
        >
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Effective data visualization is crucial for making your story compelling and easy to understand. Choose the right type of visualization for your data and ensure it supports your narrative.
            </p>
          </div>

          <PlaybookCard className="mt-6">
            <h3 className="text-white font-semibold mb-4">Visualization Tips:</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Choose the Right Chart Type:</strong>
                  <p className="text-gray-300 mt-2">
                    Use bar charts for comparisons, line charts for trends, pie charts for proportions, and scatter plots for relationships.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Keep it Simple:</strong>
                  <p className="text-gray-300 mt-2">
                    Avoid cluttering your charts with too much information. Focus on the key message you want to convey.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Use Color Effectively:</strong>
                  <p className="text-gray-300 mt-2">
                    Use color to highlight important data points or to group related information. Ensure there's enough contrast for readability.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Add Context:</strong>
                  <p className="text-gray-300 mt-2">
                    Include titles, labels, and annotations to help your audience understand the data. Explain any anomalies or significant trends.
                  </p>
                </div>
              </li>
            </ul>
          </PlaybookCard>

          <PlaybookImage
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/data-storytelling/techniques-for-storytelling-with-data.png"
            alt="Data Visualization Example"
            className="mt-6"
            caption="Techniques for Storytelling with Data"
            href="https://www.youtube.com/watch?v=BbenjdlG5g8"
          />

          <ToolCallout
            tool="youtube"
            label="YouTube: Data Storytelling Techniques"
            href="https://www.youtube.com/watch?v=BbenjdlG5g8"
            description="Watch a video on effective techniques for storytelling with data."
          />
        </PlaybookSection>

        <PlaybookSection
          title="Present with Impact"
          icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
          heading="Presentation Skills"
        >
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              How you present your data story can significantly impact its effectiveness. Focus on clarity, confidence, and engagement to make your presentation memorable.
            </p>
          </div>

          <PlaybookCard className="mt-6">
            <h3 className="text-white font-semibold mb-4">Presentation Tips:</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Know Your Audience:</strong>
                  <p className="text-gray-300 mt-2">
                    Tailor your presentation to the interests and knowledge level of your audience. Use language and examples they can relate to.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Practice:</strong>
                  <p className="text-gray-300 mt-2">
                    Rehearse your presentation multiple times to ensure smooth delivery. Pay attention to your pacing, tone, and body language.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Engage Your Audience:</strong>
                  <p className="text-gray-300 mt-2">
                    Encourage questions and discussion. Use interactive elements like polls or quizzes to keep your audience engaged.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <strong>Be Confident:</strong>
                  <p className="text-gray-300 mt-2">
                    Confidence comes from preparation. Know your data and story inside out, and be ready to address any questions or challenges.
                  </p>
                </div>
              </li>
            </ul>
          </PlaybookCard>

          <ExpandableCallout
            title="Presentation Checklist"
            type="info"
            defaultOpen={true}
            className="mt-6"
          >
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <strong>Content:</strong> Is your story clear and compelling? Does it have a strong hook, context, challenge, solution, and call to action?
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <strong>Visuals:</strong> Are your charts and graphs clear and supportive of your narrative? Do they highlight key insights?
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <strong>Delivery:</strong> Have you practiced your presentation? Are you confident in your ability to engage the audience?
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <strong>Q&A:</strong> Are you prepared to answer questions about your data and story? Do you have additional data or examples ready?
              </li>
            </ul>
          </ExpandableCallout>
        </PlaybookSection>
      </PlaybookSection>
    </div>
  );
};

export default StorytellingWithDataStage2; 